import { ClipboardCheck, Eye, FileText, History, Landmark, ListChecks } from 'lucide-react'
import { useNavigate } from 'react-router'
import { PATHS } from '@/app/router/routes'
import { ProjectBadge } from '@/features/projects/components/project-badge'
import { Permission } from '@/features/roles/consts/permissions'
import { QueryErrorState } from '@/shared/components/query-error-state/query-error-state'
import {
  type SidePanelAction,
  SidePanelActions
} from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { SidePanelFooter } from '@/shared/components/side-panel/side-panel-footer'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { cn } from '@/shared/lib/utils'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Badge } from '@/shared/ui/badge'
import { Skeleton } from '@/shared/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { useCreditDetailQuery } from '../hooks/use-credits-admin-v2-query'
import {
  CREDIT_ROW_STATUS_COLORS,
  CREDIT_ROW_STATUS_LABELS,
  type CreditListRow,
  FINANCING_TYPE_LABELS,
  formatCreditInstant
} from '../models/credit-list'
import { describeCreditPosition } from '../operations/credit-detail-view'
import { CreditAuditLog } from './credit-audit-log'
import { CreditDocumentsList } from './credit-documents-list'
import { CreditNotStartedPanel } from './credit-not-started-panel'
import { CreditStageTimeline } from './credit-stage-timeline'

function CreditDetailSkeleton() {
  return (
    <div className="flex flex-col gap-3 p-3" aria-busy="true">
      <Skeleton className="h-24 w-full rounded-md" />
      <Skeleton className="h-10 w-full rounded-md" />
      <Skeleton className="h-56 w-full rounded-md" />
    </div>
  )
}

/**
 * El trámite de una negociación cerrada — exista el expediente o no.
 *
 * ── SE DIRECCIONA POR LA FILA, NO POR EL EXPEDIENTE ──────────────────────────
 *
 * El panel recibe la FILA (`neg_`) y no un `crd_`, porque la fila es lo único que existe en los dos
 * estados: el listado son negociaciones cerradas y el expediente es opcional. Direccionarlo por el
 * `crd_` obligaba a que las filas sin expediente no abrieran nada —el clic no hacía nada— y a
 * colgar la acción de una columna, fuera de la convención del producto.
 *
 * Y le da la transición gratis: al abrirse el expediente, el listado se refresca, la fila pasa a
 * traer su `credit` y **este mismo panel** se convierte en la ficha, sin tocar la URL.
 */
export function CreditDetailPanel({ row }: { row: CreditListRow }) {
  const navigate = useNavigate()
  const closePanel = useSidePanelStore((state) => state.closePanel)
  const creditId = row.credit?.id ?? null
  const query = useCreditDetailQuery(creditId)

  // Todavía no hay expediente: la otra mitad del panel, con el botón que lo abre.
  if (!creditId) return <CreditNotStartedPanel row={row} />

  if (query.isLoading) return <CreditDetailSkeleton />

  if (query.isError || !query.data) {
    return (
      <div className="p-3">
        <QueryErrorState
          error={query.error}
          resource="el crédito"
          onRetry={() => {
            void query.refetch()
          }}
        />
      </div>
    )
  }

  const detail = query.data
  const approved = detail.status === 'approved'
  const open = (path: string) => {
    closePanel()
    navigate(path)
  }
  const actions: SidePanelAction[] = [
    {
      label: 'Abrir expediente',
      icon: <Landmark aria-hidden="true" className="size-4" />,
      onClick: () => open(PATHS.admin.credits.detail(detail.id)),
      primary: !approved
    },
    ...(approved
      ? [
          {
            label: 'Abrir Postventa',
            icon: <ClipboardCheck aria-hidden="true" className="size-4" />,
            onClick: () =>
              open(
                PATHS.admin.deliveries.fromNegotiation(
                  detail.negotiation.id,
                  detail.negotiation.code
                )
              ),
            primary: true
          }
        ]
      : [])
  ]

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Dónde va
              </p>
              <p className="mt-1 text-sm font-medium leading-5">{describeCreditPosition(detail)}</p>
            </div>
            <Badge
              variant="secondary"
              className={cn('shrink-0 border-0', CREDIT_ROW_STATUS_COLORS[detail.status])}
            >
              {CREDIT_ROW_STATUS_LABELS[detail.status]}
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-2 rounded-md bg-secondary/70 p-2.5 text-xs">
            <div>
              <p className="text-muted-foreground">Inmueble</p>
              <p className="mt-0.5 truncate font-medium">
                {detail.negotiation.property?.name ?? '—'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Proyecto</p>
              <ProjectBadge
                name={detail.negotiation.project?.name}
                code={detail.negotiation.project?.code}
                className="mt-0.5"
              />
            </div>
            <div>
              <p className="text-muted-foreground">Modalidad</p>
              <p className="mt-0.5 font-medium">
                {detail.financingType ? FINANCING_TYPE_LABELS[detail.financingType] : 'Por definir'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Inicio</p>
              <p className="mt-0.5 font-medium">{formatCreditInstant(detail.startedAt)}</p>
            </div>
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions actions={actions} />
      </SidePanelSection>

      <Tabs defaultValue="route" className="w-full overflow-hidden">
        <SidePanelSection compact>
          <TabsList className="grid h-auto w-full grid-cols-3 gap-1 rounded-md bg-secondary/60 p-1">
            <TabsTrigger value="route" className="min-h-9 gap-1 px-1 text-xs">
              <ListChecks aria-hidden="true" className="size-3.5" />
              Recorrido
            </TabsTrigger>
            <TabsTrigger value="documents" className="min-h-9 gap-1 px-1 text-xs">
              <FileText aria-hidden="true" className="size-3.5" />
              Documentos
            </TabsTrigger>
            <TabsTrigger value="activity" className="min-h-9 gap-1 px-1 text-xs">
              <History aria-hidden="true" className="size-3.5" />
              Actividad
            </TabsTrigger>
          </TabsList>
        </SidePanelSection>

        <SidePanelSection separated={false}>
          <TabsContent value="route" className="mt-0">
            <CreditStageTimeline stages={detail.stages} />
          </TabsContent>
          <TabsContent value="documents" className="mt-0">
            <CreditDocumentsList id={detail.id} status={detail.status} stages={detail.stages} />
          </TabsContent>
          <TabsContent value="activity" className="mt-0">
            <CreditAuditLog creditId={detail.id} className="px-0 py-0" />
          </TabsContent>
        </SidePanelSection>
      </Tabs>
    </div>
  )
}

/**
 * Los permisos que convierten a alguien en GESTOR del expediente. Tener uno solo alcanza: quien
 * puede mover el trámite —aunque sea en un único aspecto— no está en «vista de solo lectura».
 *
 * Los dos permisos de CONSULTA (`ReadCredits`, `ReadAllCredits`) no están en la lista a propósito:
 * son los que tienen todos los que ven esta pantalla, y meterlos acá apagaría el pie para todo el
 * mundo.
 */
const CREDIT_MANAGEMENT_PERMISSIONS = [
  Permission.StartCreditProcess,
  Permission.UpdateCreditChecklist,
  Permission.AdvanceCreditStage,
  Permission.ManageCreditDocuments
]

/**
 * El pie de «vista de solo lectura».
 *
 * ── SÓLO PARA QUIEN DE VERDAD NO PUEDE TOCAR NADA ────────────────────────────
 *
 * Aparece únicamente para el perfil de CONSULTA PURA: puede leer —`ReadCredits`, con o sin el
 * ancho— y no tiene ninguno de los permisos de gestión. Antes se mostraba siempre que la fila tuviera expediente, así que un
 * administrador —que puede iniciar, marcar, avanzar y notificar— leía que su vista era de solo
 * lectura. Un aviso que le miente a quien SÍ puede operar es peor que no tener aviso: enseña a
 * ignorar los avisos.
 *
 * ⚠️ `usePermissions` devuelve `true` en todo para las cuentas admin, así que ellas caen por
 * `hasAny` y nunca ven el pie.
 *
 * ── EL PIE NO LLEVA LÍNEA ARRIBA ─────────────────────────────────────────────
 *
 * `SidePanelFooter` trae `border-t` por defecto porque su caso normal es una BOTONERA, que necesita
 * separarse del contenido scrolleable. Acá el pie es una nota al pie, no una zona de acción, y la
 * línea la convertía visualmente en una. Se apaga con `border-t-0` DESDE EL CONSUMIDOR: el default
 * del primitivo no se toca porque lo comparten todos los demás paneles.
 *
 * El pie sólo aplica al expediente ABIERTO: «vista de solo lectura» sería falso sobre un panel cuya
 * única razón de ser es una acción de escritura.
 */
export function CreditDetailPanelFooter({ row }: { row: CreditListRow }) {
  const { hasAny } = usePermissions()

  if (!row.credit) return null
  // Cualquiera de los dos permisos de consulta: los dos abren la pantalla. Con sólo el ancho, el
  // asesor que ve su propia cartera —que es justo el perfil de consulta pura— se quedaba sin el pie
  // que lo describe; con sólo el base, se lo perdía quien mira todo el tablero sin poder operarlo.
  if (!hasAny([Permission.ReadCredits, Permission.ReadAllCredits])) return null
  if (hasAny(CREDIT_MANAGEMENT_PERMISSIONS)) return null

  return (
    <SidePanelFooter className="flex items-center gap-2 border-t-0 text-xs text-muted-foreground">
      <Eye aria-hidden="true" className="size-4 shrink-0" />
      <span>Vista de solo lectura. Administración gestiona este trámite.</span>
    </SidePanelFooter>
  )
}

export const creditDetailPanelIcon = <Landmark aria-hidden="true" />
